// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
import { logger } from '@/shared/observability/logger';
// ============================================================================
// COLLECTION API SERVICE
// ============================================================================
// API-integrated service for Collections feature
// Connects to backend Collections endpoints from Swagger API

import axios, { AxiosInstance } from 'axios';
import UnifiedAuthStorage from './unified-auth.service';
import ENV from '../config/legacy-env.js';

// ============================================================================
// TYPES
// ============================================================================

export interface Collection {
  id: string;
  collectionId: string; // Backend uses collectionId, keep both for compatibility
  name: string;
  collectionName?: string; // Backend may use collectionName instead of name
  description?: string;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean; // For "All Saved" collection
}

export interface CollectionItem {
  id: string;
  collectionId: string;
  brandId: string;
  searchId: string;
  domain: string;
  brandName: string;
  brandWebsite?: string;
  favicon?: string;
  status: 'completed' | 'processing' | 'failed';
  completeness: number;
  addedAt: string;
  notes?: string;
  logos: {
    primary: boolean;
    secondary: boolean;
    icon: boolean;
  };
  colors: {
    detected: boolean;
    count: number;
  };
  fonts: {
    detected: boolean;
    count: number;
  };
  metadata: {
    detected: boolean;
  };
  guidelines: {
    detected: boolean;
  };
}

export interface CreateCollectionRequest {
  collectionName: string;
  description?: string;
}

export interface UpdateCollectionRequest {
  collectionName?: string;
  description?: string;
}

export interface AddItemToCollectionRequest {
  brandId: number;
  notes?: string;
}

export interface CollectionItemsResponse {
  collection: Collection;
  items: {
    content: CollectionItem[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = ENV.API_BASE_URL;
const API_TIMEOUT = 30000; // 30 seconds

// Get JWT token from UnifiedAuthStorage
const getAuthToken = (): string | null => {
  return UnifiedAuthStorage.getToken();
};

// Create axios instance with authentication
const createAuthenticatedAxios = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add the access token. Who is asking is the token alone: no user, company or
  // brand id is sent, because the server would not trust one.
  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Log in development
      if (import.meta.env.DEV) {
        logger.debug(`[CollectionAPI] ${config.method?.toUpperCase()} ${config.url}`);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  );

  // Response interceptor - Handle errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;

      // Handle unauthorized
      if (status === 401) {
        logger.error('[CollectionAPI] Unauthorized - Session expired');
        // Let SessionValidator handle session expiry
      }

      // Log errors in development
      if (import.meta.env.DEV) {
        logger.error('[CollectionAPI] Error:', {
          status,
          message: error.response?.data?.detail || error.message,
          url: error.config?.url,
        });
      }

      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  );

  return instance;
};

const axiosInstance = createAuthenticatedAxios();

// ============================================================================
// COLLECTION API SERVICE CLASS
// ============================================================================

const COLLECTIONS = '/api/v1/collections';

/** The backend answers with an RFC 9457 problem: `detail` is the sentence for a person. */
const failure = (error: any, fallback: string) => ({
  success: false,
  error: error?.response?.data?.detail || fallback,
});

/**
 * Whether a collection id is usable. A request built from a missing one reads `/collections/null/items`, which
 * the server can only answer 404; it is not sent, and the stack says which caller had no id.
 */
const hasId = (collectionId: unknown, caller: string): collectionId is string => {
  const usable = typeof collectionId === 'string' && collectionId !== '' && collectionId !== 'null' && collectionId !== 'undefined';
  if (!usable) {
    logger.warn(`[CollectionAPI] ${caller} called without a collection id; nothing was sent`, new Error().stack);
  }
  return usable;
};

/** A collection as the API returns it, with the names this application's screens read. */
const toCollection = (collection: any): Collection => ({
  ...collection,
  collectionId: collection.id,
  name: collection.name,
  collectionName: collection.name,
});

const hostOf = (website?: string | null): string => {
  if (!website) return '';
  try {
    return new URL(website.includes('://') ? website : `https://${website}`).hostname;
  } catch {
    return website;
  }
};

/** A saved brand as the API returns it, in the shape the screens read. */
const toItem = (item: any): CollectionItem => {
  const domain = hostOf(item.brandWebsite);
  return {
    id: item.id,
    collectionId: item.collectionId,
    brandId: String(item.brandId),
    searchId: String(item.brandId),
    domain,
    brandName: item.brandName || 'Unknown Brand',
    brandWebsite: item.brandWebsite,
    favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
    status: 'completed',
    completeness: 100,
    addedAt: item.addedAt,
    notes: item.notes ?? undefined,
    logos: { primary: Boolean(item.logoUrl), secondary: false, icon: Boolean(item.hasIcon) },
    colors: { detected: item.colorCount > 0, count: item.colorCount ?? 0 },
    fonts: { detected: item.fontCount > 0, count: item.fontCount ?? 0 },
    metadata: { detected: true },
    guidelines: { detected: false },
  };
};

// Items come by cursor, a hundred at a time. The screens page them in the browser, so every page is
// read, up to this many, which is more brands than anyone keeps in a collection.
const ITEMS_PAGE_SIZE = 100;
const MAX_ITEM_PAGES = 10;

class CollectionApiServiceClass {
  /** GET /api/v1/collections: the caller's collections, the default one first. */
  async getCollections(): Promise<ApiResponse<Collection[]>> {
    try {
      const response = await axiosInstance.get(COLLECTIONS);
      const collections: any[] = Array.isArray(response.data) ? response.data : [];
      return { success: true, data: collections.map(toCollection) };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to fetch collections:', error);
      return failure(error, 'Failed to load collections');
    }
  }

  /**
   * GET /api/v1/collections/{id}/items: every brand in one collection, newest first.
   *
   * The paging arguments the screens still pass are ignored: this follows the cursor to the end.
   */
  async getCollectionItems(collectionId: string = ''): Promise<ApiResponse<CollectionItem[]>> {
    if (!hasId(collectionId, 'getCollectionItems')) {
      return { success: true, data: [] };
    }
    try {
      const items: CollectionItem[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_ITEM_PAGES; page += 1) {
        const response = await axiosInstance.get(`${COLLECTIONS}/${collectionId}/items`, {
          params: { size: ITEMS_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        });
        const content: any[] = Array.isArray(response.data?.content) ? response.data.content : [];
        items.push(...content.map(toItem));
        cursor = response.data?.hasMore ? response.data?.nextCursor : undefined;
        if (!cursor) break;
      }
      return { success: true, data: items };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to fetch collection items:', error);
      return failure(error, 'Failed to load collection items');
    }
  }

  /** POST /api/v1/collections */
  async createCollection(data: CreateCollectionRequest): Promise<ApiResponse<Collection>> {
    try {
      const response = await axiosInstance.post(COLLECTIONS, {
        name: data.collectionName,
        description: data.description,
      });
      return { success: true, data: toCollection(response.data) };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to create collection:', error);
      return failure(error, 'Failed to create collection');
    }
  }

  /** PUT /api/v1/collections/{id} */
  async updateCollection(
    collectionId: string,
    data: UpdateCollectionRequest
  ): Promise<ApiResponse<Collection>> {
    try {
      const response = await axiosInstance.put(`${COLLECTIONS}/${collectionId}`, {
        name: data.collectionName,
        description: data.description,
      });
      return { success: true, data: toCollection(response.data) };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to update collection:', error);
      return failure(error, 'Failed to update collection');
    }
  }

  /** DELETE /api/v1/collections/{id} */
  async deleteCollection(collectionId: string): Promise<ApiResponse<void>> {
    try {
      await axiosInstance.delete(`${COLLECTIONS}/${collectionId}`);
      return { success: true, message: 'Collection deleted successfully' };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to delete collection:', error);
      return failure(error, 'Failed to delete collection');
    }
  }

  /** POST /api/v1/collections/{id}/items. The server also saves it in "All Saved". */
  async addItemToCollection(
    collectionId: string,
    item: AddItemToCollectionRequest
  ): Promise<ApiResponse<CollectionItem>> {
    if (!hasId(collectionId, 'addItemToCollection')) {
      return { success: false, error: 'Choose a collection first.' };
    }
    try {
      const response = await axiosInstance.post(`${COLLECTIONS}/${collectionId}/items`, item);
      return { success: true, data: toItem(response.data) };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to add item to collection:', error);
      return {
        ...failure(error, 'Failed to add item to collection'),
        // Lets a caller treat "already there" as done: the brand is where it was asked to be.
        alreadyThere: error?.response?.status === 409,
      };
    }
  }

  /** DELETE /api/v1/collections/{id}/items/{brandId}. Out of "All Saved" it goes from every collection. */
  async removeItemFromCollection(collectionId: string, brandId: string): Promise<ApiResponse<void>> {
    if (!hasId(collectionId, 'removeItemFromCollection')) {
      return { success: false, error: 'Choose a collection first.' };
    }
    try {
      await axiosInstance.delete(`${COLLECTIONS}/${collectionId}/items/${brandId}`);
      return { success: true, message: 'Item removed successfully' };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to remove item from collection:', error);
      return failure(error, 'Failed to remove item');
    }
  }

  /**
   * Saves a brand in several collections.
   *
   * One after another, not together: saving in a custom collection also saves in "All Saved", so a request for
   * both at once would have the two reach for the same place. A brand that is already in one of them is not a
   * failure; it is where it was asked to be.
   */
  async addItemToMultipleCollections(
    collectionIds: string[],
    item: AddItemToCollectionRequest
  ): Promise<ApiResponse<void>> {
    let failed = 0;
    for (const collectionId of collectionIds) {
      const result: any = await this.addItemToCollection(collectionId, item);
      if (!result.success && !result.alreadyThere) {
        failed += 1;
      }
    }
    if (failed > 0) {
      return { success: false, error: `Failed to add to ${failed} collection(s)` };
    }
    return { success: true, message: 'Item added to all selected collections' };
  }

  /** GET /api/v1/collections/brands/{brandId}: the ids of the caller's collections that hold the brand. */
  async getItemCollections(brandId: string): Promise<ApiResponse<string[]>> {
    try {
      const response = await axiosInstance.get(`${COLLECTIONS}/brands/${brandId}`);
      return { success: true, data: response.data?.collectionIds ?? [] };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to check item collections:', error);
      return failure(error, 'Failed to check collections');
    }
  }

  /** POST /api/v1/collections/default: "All Saved", created the first time and never twice. */
  async getOrCreateDefaultCollection(): Promise<ApiResponse<Collection>> {
    try {
      const response = await axiosInstance.post(`${COLLECTIONS}/default`);
      return { success: true, data: toCollection(response.data) };
    } catch (error: any) {
      logger.error('[CollectionAPI] Failed to get the default collection:', error);
      return failure(error, 'Failed to initialize default collection');
    }
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const CollectionApiService = new CollectionApiServiceClass();
export default CollectionApiService;
